// src/pages/NewProductNeedAccepted.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './NewProductNeedAccepted.css';
import { API_URL } from "../config";

function NewProductNeedAccepted({ currentUser }) {
    const [products, setProducts] = useState([]);
    const [editingProduct, setEditingProduct] = useState(null);
    const [formData, setFormData] = useState({});
    const [categories, setCategories] = useState([]);
    const [locations, setLocations] = useState([]);
    const navigate = useNavigate();

    const canApprove = ['Admin', 'Moderator'].includes(currentUser.role) || 
                      ['Szef', 'Logistyk'].includes(currentUser.position);

    useEffect(() => {
        fetchNewProducts();
        fetchCategories();
        fetchLocations();
    }, []);

    const fetchNewProducts = async () => {
        try {
            const response = await fetch(`${API_URL}/products?status=Nowy`, {
                credentials: 'include'
            });
            if (!response.ok) throw new Error('Błąd pobierania produktów');
            const data = await response.json();
            setProducts(data);
        } catch (error) {
            console.error('Błąd:', error);
        }
    };

    const fetchCategories = async () => {
        try {
            const response = await fetch(`${API_URL}/categories`, {
                credentials: 'include'
            });
            if (!response.ok) throw new Error('Błąd pobierania kategorii');
            const data = await response.json();
            setCategories(data);
        } catch (error) {
            console.error('Błąd:', error);
        }
    };

    const fetchLocations = async () => {
        try {
            const response = await fetch(`${API_URL}/locations`, {
                credentials: 'include'
            });
            if (!response.ok) throw new Error('Błąd pobierania lokalizacji');
            const data = await response.json();
            setLocations(data);
        } catch (error) {
            console.error('Błąd:', error);
        }
    };

    const handleApprove = (product) => {
        if (!canApprove) return;
        setEditingProduct(product);
        setFormData({
            category_id: product.category_id,
            name: product.name,
            description: product.description,
            manufacturer: product.manufacturer || '',
            quantity: product.quantity || 0,
            catalog_number: product.catalog_number || '',
            serial_number: product.serial_number || '',
            unit_of_measure: product.unit_of_measure || '',
            measure: product.measure || '',
            purchase_link: product.purchase_link || '',
            location_id: product.location_id || '',
            price: product.price || '',
            notes: product.notes || ''
        });
    };

    const handleReject = async (id) => {
        if (!canApprove) return;
        if (window.confirm('Czy na pewno chcesz usunąć ten produkt?')) {
            try {
                const response = await fetch(`${API_URL}/products/${id}`, {
                    method: 'DELETE',
                    credentials: 'include'
                });
                if (!response.ok) throw new Error('Błąd usuwania produktu');
                fetchNewProducts();
            } catch (error) {
                console.error('Błąd:', error);
            }
        }
    };

    const handleEditChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleEditSubmit = async (e) => {
        e.preventDefault();
        try {
            const statusResponse = await fetch(`${API_URL}/statuses`, {
                credentials: 'include'
            });
            const statuses = await statusResponse.json();
            const statusId = statuses.find(s => s.name === 'Gotowy').id;

            const updatedData = { ...formData, status_id: statusId };
            const response = await fetch(`${API_URL}/products/${editingProduct.id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify(updatedData)
            });
            if (!response.ok) throw new Error('Błąd aktualizacji produktu');
            setEditingProduct(null);
            fetchNewProducts();
        } catch (error) {
            console.error('Błąd:', error);
        }
    };

    return (
        <div className="new-product-need-accepted">
            <h2>Nowe Produkty do Zatwierdzenia</h2>
            {editingProduct ? (
                <form onSubmit={handleEditSubmit} className="edit-product-form">
                    <div className="form-group">
                        <label>Kategoria:</label>
                        <select name="category_id" value={formData.category_id} onChange={handleEditChange} required>
                            <option value="">Wybierz kategorię</option>
                            {categories.map(cat => (
                                <option key={cat.id} value={cat.id}>{cat.name}</option>
                            ))}
                        </select>
                    </div>
                    <div className="form-group">
                        <label>Nazwa:</label>
                        <input type="text" name="name" value={formData.name} onChange={handleEditChange} required />
                    </div>
                    <div className="form-group">
                        <label>Opis:</label>
                        <textarea name="description" value={formData.description} onChange={handleEditChange} required />
                    </div>
                    <div className="form-group">
                        <label>Producent:</label>
                        <input type="text" name="manufacturer" value={formData.manufacturer} onChange={handleEditChange} />
                    </div>
                    <div className="form-group">
                        <label>Ilość:</label>
                        <input type="number" name="quantity" value={formData.quantity} onChange={handleEditChange} min="0" />
                    </div>
                    <div className="form-group">
                        <label>Numer katalogowy:</label>
                        <input type="text" name="catalog_number" value={formData.catalog_number} onChange={handleEditChange} />
                    </div>
                    <div className="form-group">
                        <label>Numer seryjny:</label>
                        <input type="text" name="serial_number" value={formData.serial_number} onChange={handleEditChange} />
                    </div>
                    <div className="form-group">
                        <label>Jednostka miary:</label>
                        <input type="text" name="unit_of_measure" value={formData.unit_of_measure} onChange={handleEditChange} />
                    </div>
                    <div className="form-group">
                        <label>Miara:</label>
                        <input type="number" name="measure" value={formData.measure} onChange={handleEditChange} step="0.01" />
                    </div>
                    <div className="form-group">
                        <label>Link zakupu:</label>
                        <input type="text" name="purchase_link" value={formData.purchase_link} onChange={handleEditChange} />
                    </div>
                    <div className="form-group">
                        <label>Lokalizacja:</label>
                        <select name="location_id" value={formData.location_id} onChange={handleEditChange}>
                            <option value="">Wybierz lokalizację</option>
                            {locations.map(loc => (
                                <option key={loc.id} value={loc.id}>{loc.name}</option>
                            ))}
                        </select>
                    </div>
                    <div className="form-group">
                        <label>Cena:</label>
                        <input type="number" name="price" value={formData.price} onChange={handleEditChange} step="0.01" />
                    </div>
                    <div className="form-group">
                        <label>Uwagi:</label>
                        <textarea name="notes" value={formData.notes} onChange={handleEditChange} />
                    </div>
                    <div className="modal-buttons">
                        <button type="submit" className="btn-submit">Zatwierdź</button>
                        <button type="button" className="btn-submit" onClick={() => setEditingProduct(null)}>Anuluj</button>
                    </div>
                </form>
            ) : (
                <table className="products-table">
                    <thead>
                        <tr>
                            <th>Nazwa</th>
                            <th>Kategoria</th>
                            {canApprove && <th>Akcje</th>}
                        </tr>
                    </thead>
                    <tbody>
                        {products.map(product => (
                            <tr key={product.id}>
                                <td>{product.name}</td>
                                <td>{product.category_name}</td>
                                {canApprove && (
                                    <td>
                                        <button className="btn-action" onClick={() => handleApprove(product)}>Zatwierdź</button>
                                        <button className="btn-action btn-reject" onClick={() => handleReject(product.id)}>Odrzuć</button>
                                    </td>
                                )}
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
        </div>
    );
}

export default NewProductNeedAccepted;